import { useTranslation } from 'react-i18next';
import type { WeekKpiSummary } from '../../../types/weekOverview';
import { KPI_ACCENT_COLORS, KPI_DISTRIBUTION_COLORS } from '../../../constants/weekOverviewConstants';
import { KpiBox } from '../KpiBox/KpiBox';
import {
  DistributionIcon,
  OccupancyIcon,
  PatientsIcon,
  StaffIcon,
  TreatmentsIcon
} from './kpiIcons';
import styles from './WeekKpiCards.module.css';

interface WeekKpiCardsProps {
  kpis: WeekKpiSummary;
}

export const WeekKpiCards = ({ kpis }: WeekKpiCardsProps) => {
  const { t } = useTranslation();

  const distribution = kpis.taskDistribution;
  const distributionTotal =
    distribution.interventionHours +
    distribution.vacationHours +
    distribution.otherBookingHours +
    distribution.whiteTimeHours;

  const occupancyPercent =
    kpis.occupancyAvailableHours > 0
      ? Math.round((kpis.occupancyBookedHours / kpis.occupancyAvailableHours) * 100)
      : 0;

  const hoursUnit = t('week-overview-kpi-hours-unit');

  const legendItems = [
    {
      color: KPI_DISTRIBUTION_COLORS.interventions,
      label: t('week-overview-kpi-task-interventions'),
      value: distribution.interventionHours
    },
    {
      color: KPI_DISTRIBUTION_COLORS.vacation,
      label: t('week-overview-kpi-task-vacation'),
      value: distribution.vacationHours
    },
    {
      color: KPI_DISTRIBUTION_COLORS.otherBookings,
      label: t('week-overview-kpi-task-other-bookings'),
      value: distribution.otherBookingHours
    },
    {
      color: KPI_DISTRIBUTION_COLORS.whiteTime,
      label: t('week-overview-kpi-task-white-time'),
      value: distribution.whiteTimeHours
    }
  ];

  return (
    <div className={styles.cards}>
      <KpiBox
        label={t('week-overview-kpi-task-distribution')}
        icon={<DistributionIcon />}
        accent={KPI_ACCENT_COLORS.distribution}
        feature
        value={Math.round(distributionTotal)}
        unit={hoursUnit}
        footer={
          <>
            <div className={styles.barTrack}>
              {legendItems.map((item) => {
                const pct = distributionTotal > 0 ? (item.value / distributionTotal) * 100 : 0;
                if (pct === 0) return null;
                return (
                  <span
                    key={item.label}
                    className={styles.barSegment}
                    style={{ width: `${pct}%`, background: item.color }}
                  />
                );
              })}
            </div>
            <ul className={styles.distLegend}>
              {legendItems.map((item) => (
                <li key={item.label} className={styles.distLegendItem}>
                  <span className={styles.legendDot} style={{ background: item.color }} />
                  <span className={styles.legendLabel}>{item.label}</span>
                  <span className={styles.legendValue}>
                    {Math.round(item.value)} {hoursUnit}
                  </span>
                </li>
              ))}
            </ul>
          </>
        }
      />

      <KpiBox
        label={t('week-overview-kpi-occupancy')}
        icon={<OccupancyIcon />}
        accent={KPI_ACCENT_COLORS.occupancy}
        value={occupancyPercent}
        unit="%"
      />

      <KpiBox
        label={t('week-overview-kpi-staff')}
        icon={<StaffIcon />}
        accent={KPI_ACCENT_COLORS.staff}
        value={kpis.staffCount}
      />

      <KpiBox
        label={t('week-overview-kpi-interventions')}
        icon={<TreatmentsIcon />}
        accent={KPI_ACCENT_COLORS.treatments}
        value={kpis.interventionCount}
      />

      <KpiBox
        label={t('week-overview-kpi-patients')}
        icon={<PatientsIcon />}
        accent={KPI_ACCENT_COLORS.patients}
        value={kpis.patientsAdmitted}
        unit={`/ ${kpis.patientsTotal}`}
      />
    </div>
  );
};
